import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { EventPayload, ServerSideEventType } from '@/hooks/api/sse/types.ts';
import { reportException } from '@/services/sentry.ts';

const RUN_EVENT_BATCH_WINDOW_MS = 250;

export default function useHandleEvent(
  getKeysToInvalidate: (type: ServerSideEventType, data: EventPayload) => unknown[][] | undefined,
) {
  const queryClient = useQueryClient();

  const pendingQueryKeysRef = useRef<Set<string>>(new Set());
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushEvents = useCallback(async () => {
    const keysToInvalidate = Array.from(pendingQueryKeysRef.current);
    pendingQueryKeysRef.current.clear();

    await Promise.all(
      keysToInvalidate.map(async (key) => {
        try {
          const parsedKey = JSON.parse(key); // restore the original query key
          await queryClient.invalidateQueries({ queryKey: parsedKey });
        } catch (err) {
          reportException(err);
          console.error('Failed to parse query key for invalidation:', key, err);
        }
      }),
    );

    timeoutRef.current = null;

    if (pendingQueryKeysRef.current.size > 0) {
      timeoutRef.current = setTimeout(flushEvents, RUN_EVENT_BATCH_WINDOW_MS);
    }
  }, [queryClient]);

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      pendingQueryKeysRef.current.clear();
    },
    [],
  );

  return useCallback(
    (type: ServerSideEventType, event: EventPayload) => {
      // console.log('[SSE] Event:', type, event);

      const keysToInvalidate = getKeysToInvalidate(type, event)?.map((k) => JSON.stringify(k));
      if (!keysToInvalidate || !keysToInvalidate.length) return;

      // console.log('[SSE] Keys to invalidate:', keysToInvalidate);

      pendingQueryKeysRef.current = new Set([...pendingQueryKeysRef.current, ...keysToInvalidate]);

      if (timeoutRef.current === null) {
        timeoutRef.current = setTimeout(flushEvents, RUN_EVENT_BATCH_WINDOW_MS);
      }
    },
    [flushEvents, getKeysToInvalidate],
  );
}
